import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Empty,
  InputNumber,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import cancelX from "../../../Assets/svg/cancelXbutton.svg";
import deleteIcon from "../../../Assets/svg/table-action-delete.svg";
import {
  getStalledThresholds,
  upsertStalledThreshold,
  deleteStalledThreshold,
} from "../../../services/features/dashboard.service";
import { taskTypes } from "../../../services/Enums/taskStatusesEnum";
import "./stalledThresholds.scss";

// F-03 — stalled threshold configuration (SystemAdmin / ProjectManager).
//
// The escalation worker marks a task/visit as stalled after N days with no
// update. N is resolved project → task type → platform default, so this screen
// owns the middle tier: one row per task type that has an override, plus an
// "add override" row for the types that fall back to the default.
//
// Mirrors the pattern-threshold editor in the AI Assistant (inline InputNumber
// per row + Save) and the platform's table action icons, so the two threshold
// screens read the same way.

// Platform default when neither the project nor the task type sets a value.
const DEFAULT_THRESHOLD_DAYS = 5;

const StalledThresholdsModal = ({ open, onClose, notify }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [removingKey, setRemovingKey] = useState(null);
  const [newType, setNewType] = useState(undefined);
  const [newDays, setNewDays] = useState(DEFAULT_THRESHOLD_DAYS);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getStalledThresholds(
      (res) => {
        const list = res?.data || res || [];
        const configured = Array.isArray(list) ? list : [];
        setRows(configured);
        const next = {};
        configured.forEach((r) => {
          next[r.taskType] = r.thresholdDays;
        });
        setEdits(next);
        setLoading(false);
      },
      () => {
        setRows([]);
        setLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Task types with no override — they use the default until one is added.
  const unconfigured = useMemo(() => {
    const taken = new Set(rows.map((r) => Number(r.taskType)));
    return Object.entries(taskTypes)
      .map(([value, label]) => ({ value: Number(value), label }))
      .filter((t) => !taken.has(t.value));
  }, [rows]);

  const handleSave = (row) => {
    const days = edits[row.taskType];
    if (!days || days < 1) {
      notify?.(
        "danger",
        "Invalid threshold",
        "The threshold must be at least 1 day."
      );
      return;
    }
    setSavingKey(row.taskType);
    upsertStalledThreshold(
      { taskType: row.taskType, thresholdDays: days },
      () => {
        setSavingKey(null);
        notify?.(
          "success",
          "Threshold saved",
          `${row.taskTypeLabel || taskTypes[row.taskType]} now stalls after ${days} day${
            days === 1 ? "" : "s"
          }.`
        );
        load();
      },
      () => {
        setSavingKey(null);
        notify?.("danger", "Save failed", "Couldn't save the threshold.");
      }
    );
  };

  const handleAdd = () => {
    if (!newType || !newDays || newDays < 1) {
      notify?.(
        "danger",
        "Missing values",
        "Pick a task type and a threshold of at least 1 day."
      );
      return;
    }
    setAdding(true);
    upsertStalledThreshold(
      { taskType: newType, thresholdDays: newDays },
      () => {
        setAdding(false);
        notify?.(
          "success",
          "Override added",
          `${taskTypes[newType]} now stalls after ${newDays} day${
            newDays === 1 ? "" : "s"
          }.`
        );
        setNewType(undefined);
        setNewDays(DEFAULT_THRESHOLD_DAYS);
        load();
      },
      () => {
        setAdding(false);
        notify?.("danger", "Couldn't add", "Couldn't add this override.");
      }
    );
  };

  const handleRemove = (row) => {
    setRemovingKey(row.taskType);
    deleteStalledThreshold(
      row.id,
      () => {
        setRemovingKey(null);
        notify?.(
          "success",
          "Override removed",
          `${row.taskTypeLabel || taskTypes[row.taskType]} is back to the ${DEFAULT_THRESHOLD_DAYS}-day default.`
        );
        load();
      },
      () => {
        setRemovingKey(null);
        notify?.("danger", "Couldn't remove", "Couldn't remove this override.");
      }
    );
  };

  const columns = [
    {
      title: <span className="styledTitle">Task Type</span>,
      dataIndex: "taskTypeLabel",
      key: "taskTypeLabel",
      render: (label, row) => (
        <span className="styledDataIndex3">
          {label || taskTypes[row.taskType] || "—"}
        </span>
      ),
    },
    {
      title: <span className="styledTitle">Threshold (Days)</span>,
      key: "thresholdDays",
      width: 160,
      render: (_, row) => (
        <InputNumber
          min={1}
          value={edits[row.taskType]}
          onChange={(v) =>
            setEdits((prev) => ({ ...prev, [row.taskType]: v }))
          }
        />
      ),
    },
    {
      title: <span className="styledTitle"></span>,
      key: "action",
      width: 150,
      render: (_, row) => (
        <div className="stalledRowActions">
          <Button
            className="inviteButtonTPM stalledRowSave"
            loading={savingKey === row.taskType}
            disabled={edits[row.taskType] === row.thresholdDays}
            onClick={() => handleSave(row)}
          >
            Save
          </Button>
          <Tooltip title="Remove override — falls back to the default">
            {removingKey === row.taskType ? (
              <Spin size="small" />
            ) : (
              <img
                src={deleteIcon}
                alt="remove"
                className="stalledRowRemove"
                onClick={() => handleRemove(row)}
              />
            )}
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={open}
      closable={false}
      onCancel={onClose}
      footer={false}
      width={760}
      destroyOnClose
      className="stalledThresholdsModal"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        <img
          src={cancelX}
          alt="close"
          style={{ marginLeft: "auto", cursor: "pointer" }}
          onClick={onClose}
        />
      </div>
      <h2 className="modalHeader">Stalled Thresholds</h2>
      <p className="modalsHeader1">Escalation Settings</p>
      <p className="stalledIntro">
        A task or site visit with no update for this many days is flagged as
        stalled and escalated to the PM. Task types without an override use the{" "}
        {DEFAULT_THRESHOLD_DAYS}-day default, unless the project round sets its
        own value.
      </p>

      {loading ? (
        <div className="stalledLoading">
          <Spin />
        </div>
      ) : (
        <>
          <Table
            className="tableTPM"
            rowKey={(r) => r.id ?? r.taskType}
            columns={columns}
            dataSource={rows}
            pagination={false}
            size="middle"
            locale={{
              emptyText: (
                <Empty description="No per-task-type overrides yet — every task type uses the default." />
              ),
            }}
          />

          {/* Add an override for a task type that still uses the default. */}
          <p className="modalsHeader1 stalledAddHeader">Add an Override</p>
          {unconfigured.length === 0 ? (
            <p className="stalledIntro">
              Every task type already has its own threshold.
            </p>
          ) : (
            <div className="stalledAddRow">
              <Select
                showSearch
                allowClear
                size="large"
                placeholder="Select a task type"
                value={newType}
                onChange={setNewType}
                optionFilterProp="label"
                options={unconfigured}
              />
              <InputNumber
                min={1}
                size="large"
                value={newDays}
                onChange={setNewDays}
                placeholder="Days"
              />
              <Button
                className="addButtonTPM"
                loading={adding}
                onClick={handleAdd}
              >
                Add Override
              </Button>
            </div>
          )}

          {/* Which types are still on the default — visible, not implied by
              absence from the table above. */}
          {unconfigured.length > 0 && (
            <div className="stalledDefaults">
              <p className="stalledIntro">
                Using the {DEFAULT_THRESHOLD_DAYS}-day default:
              </p>
              <div className="stalledDefaultPills">
                {unconfigured.map((t) => (
                  <span className="stalledDefaultPill" key={t.value}>
                    {t.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <div className="buttonsModal stalledFooter">
        <Button className="inviteButtonTPM" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
};

export default StalledThresholdsModal;
